import { specFor } from "./catalog";
import type { SWidget } from "./model";

/* display.numberFormat -- the Format pane's Power BI-style number format
 * (type, currency, display units, decimals, negatives, separator,
 * prefix/suffix). The renderer is backend/tv_preview_static/js/
 * report-widgets.js (formatWithNumberFormat); this is its TypeScript twin,
 * used only for the pane's live preview -- keep the two in lockstep. */

export type NumberUnits = "auto" | "none" | "thousands" | "millions" | "billions" | "trillions";

export interface NumberFormat {
  type?: "auto" | "number" | "currency" | "percent";
  currency?: string;
  units?: NumberUnits;
  decimals?: number;
  negative?: "minus" | "parentheses";
  thousands?: boolean;
  prefix?: string;
  suffix?: string;
  percentOf?: "auto" | "fraction" | "whole";
}

export const UNIT_OPTIONS: Array<{ value: NumberUnits; label: string }> = [
  { value: "auto", label: "Auto" },
  { value: "none", label: "None (1,234,567)" },
  { value: "thousands", label: "Thousands (K)" },
  { value: "millions", label: "Millions (M)" },
  { value: "billions", label: "Billions (B)" },
  { value: "trillions", label: "Trillions (T)" },
];

export const CURRENCY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "USD", label: "US dollar ($)" },
  { value: "EUR", label: "Euro (€)" },
  { value: "GBP", label: "British pound (£)" },
  { value: "EGP", label: "Egyptian pound (EGP)" },
  { value: "SAR", label: "Saudi riyal (SAR)" },
  { value: "AED", label: "UAE dirham (AED)" },
  { value: "KWD", label: "Kuwaiti dinar (KWD)" },
  { value: "QAR", label: "Qatari riyal (QAR)" },
];

const UNITS: Record<string, number> = { none: 1, thousands: 1e3, millions: 1e6, billions: 1e9, trillions: 1e12 };
const SUFFIX: Record<string, string> = { "1": "", "1000": "K", "1000000": "M", "1000000000": "B", "1000000000000": "T" };
const SYMBOL: Record<string, string> = { USD: "$", EUR: "€", GBP: "£", EGP: "EGP", SAR: "SAR", AED: "AED", KWD: "KWD", QAR: "QAR" };

/** Visual types whose numbers the Number format section controls. */
const NUMBERED = new Set(["kpi", "kpi_trend", "gauge_chart", "table", "matrix", "grouped_table", "bullet_chart", "scatter_chart", "heatmap_chart"]);
export function takesNumberFormat(type: string): boolean {
  return NUMBERED.has(type) || specFor(type)?.category === "Charts";
}

/** The older one-word display.format presets, read as a number format so
 * the pane starts from what the visual shows today. */
export function fromLegacyFormat(format: unknown): NumberFormat {
  switch (format) {
    case "number": return { type: "number", units: "none" };
    case "integer": return { type: "number", units: "none", decimals: 0 };
    case "decimal1": return { type: "number", units: "none", decimals: 1 };
    case "decimal2": return { type: "number", units: "none", decimals: 2 };
    case "compact": case "number_compact": return { type: "number", units: "auto" };
    case "percent": return { type: "percent", decimals: 0 };
    case "percent1": return { type: "percent", decimals: 1 };
    case "usd": return { type: "currency", currency: "USD", units: "none" };
    case "usd_compact": return { type: "currency", currency: "USD", units: "auto" };
    case "egp": return { type: "currency", currency: "EGP", units: "none" };
    case "egp_compact": return { type: "currency", currency: "EGP", units: "auto" };
    default: return {};
  }
}

/** The number format in effect for a widget (new setting, else the preset). */
export function numberFormatOf(display: Record<string, any> | undefined): NumberFormat {
  const nf = display?.numberFormat;
  return nf && typeof nf === "object" ? nf : fromLegacyFormat(display?.format);
}

function text(v: unknown, limit: number): string {
  return typeof v === "string" ? v.replace(/[<>&"]/g, "").slice(0, limit) : "";
}

function digits(n: number, decimals: number, trim: boolean, nf: NumberFormat): string {
  let fixed = n.toFixed(decimals);
  if (trim && fixed.includes(".")) fixed = fixed.replace(/0+$/, "").replace(/\.$/, "");
  const [int, dec] = fixed.split(".");
  return (nf.thousands === false ? int : int.replace(/\B(?=(\d{3})+(?!\d))/g, ",")) + (dec ? `.${dec}` : "");
}

/** Same result as the renderer's formatWithNumberFormat for one value.
 * `impliedCurrency` stands in for the renderer's automatic currency
 * detection (the widget title / measure name / older preset). */
export function formatNumber(nf: NumberFormat, value: number, opts: { units?: string; decimals?: number; impliedCurrency?: string | null } = {}): string {
  const decRaw = opts.decimals ?? nf.decimals;
  const dec = typeof decRaw === "number" && decRaw >= 0 ? Math.floor(decRaw) : null;
  const abs = Math.abs(value);
  let body: string;
  let sym = "";
  let v = value;
  if (nf.type === "percent") {
    const mul = nf.percentOf === "whole" ? 1 : nf.percentOf === "fraction" ? 100 : abs > 1.5 ? 1 : 100;
    body = digits(abs * mul, dec ?? 1, dec === null, nf);
    if (parseFloat(body.replace(/,/g, "")) === 0) v = 0;
    body += "%";
  } else {
    const units = opts.units || nf.units;
    const unit = units && UNITS[units] ? UNITS[units] : abs >= 1e12 ? 1e12 : abs >= 1e9 ? 1e9 : abs >= 1e6 ? 1e6 : abs >= 1e3 ? 1e3 : 1;
    const n = abs / unit;
    body = digits(n, dec ?? (unit === 1 ? (n % 1 === 0 ? 0 : 2) : unit === 1e3 ? 1 : 2), dec === null, nf);
    if (parseFloat(body.replace(/,/g, "")) === 0) v = 0;
    body += SUFFIX[String(unit)] ?? "";
    if (nf.type === "currency") {
      const code = text(nf.currency, 6) || "USD";
      sym = SYMBOL[code] ?? code;
    } else if (!nf.type || nf.type === "auto") {
      sym = opts.impliedCurrency === "usd" ? "$" : opts.impliedCurrency === "egp" ? "EGP" : "";
    }
  }
  let core = sym ? (/^[A-Za-z]{2,}$/.test(sym) ? `${sym} ${body}` : sym + body) : body;
  if (v < 0) core = nf.negative === "parentheses" ? `(${core})` : `-${core}`;
  return text(nf.prefix, 12) + core + text(nf.suffix, 12);
}

/** The renderer's automatic currency for a widget with no explicit type. */
export function impliedCurrency(widget: SWidget): "usd" | "egp" | null {
  const f = widget.display?.format;
  if (typeof f === "string" && f.startsWith("usd")) return "usd";
  if (typeof f === "string" && f.startsWith("egp")) return "egp";
  if (f) return null; // any other preset is authoritative (no currency)
  const probe = (s: unknown) => {
    const t = String(s ?? "").toLowerCase();
    return t.includes("egp") ? "egp" : t.includes("usd") || t.includes("us$") || t.includes("$") ? "usd" : null;
  };
  return probe(widget.title) ?? probe(widget.query?.measures?.[0]);
}
